import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Zap } from "lucide-react";
import mockupMac from "../assets/mac.png";
import mockupMobile from "../assets/mobile.png";

export default function FinalCTA() {
	const { t } = useTranslation();

	const ctaBenefits = t("cta.benefits", { returnObjects: true }) || [
		"30 días gratis",
		"Sin tarjeta",
		"Cancela cuando quieras",
	];

	return (
		<section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
			{/* Decorative elements */}
			<div className="absolute top-0 left-0 w-full h-full overflow-hidden">
				<div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
				<div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
			</div>

			<div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
				<div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16">
					{/* Texto */}
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true, amount: 0.3 }}
						className="text-center lg:text-left"
					>
						{/* Badge */}
						<div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
							<Zap className="w-4 h-4" />
							{t("cta.badge")}
						</div>

						<h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
							{t("cta.title")}
						</h2>

						<p className="text-gray-300 text-lg mb-8 max-w-lg mx-auto lg:mx-0">
							{t("cta.subtitleHonest")}
						</p>

						{/* Benefits */}
						<div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
							{ctaBenefits.map((benefit, i) => (
								<span
									key={i}
									className="inline-flex items-center gap-2 text-sm text-gray-300"
								>
									<Check className="w-4 h-4 text-primary" />
									{benefit}
								</span>
							))}
						</div>

						{/* CTA Buttons */}
						<div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
							<motion.a
								href="https://creator.applaby.com"
								target="_blank"
								rel="noreferrer"
								whileHover={{ scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
								className="group inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
							>
								{t("cta.button")}
								<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
							</motion.a>
							<Link
								to="/ejemplos"
								className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-8 py-4 rounded-xl font-semibold border border-white/20 hover:bg-white/20 transition-all duration-300"
							>
								{t("cta.secondaryButton")}
							</Link>
						</div>
					</motion.div>

					{/* Imágenes mockups */}
					<motion.div
						initial={{ opacity: 0, x: 40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true, amount: 0.3 }}
						className="relative flex justify-center overflow-visible"
					>
						{/* Portátil */}
						<img
							src={mockupMac}
							alt="Mac mockup Applaby"
							className="w-[380px] md:w-[440px] lg:w-[500px] drop-shadow-2xl relative z-10"
						/>
						{/* iPhone */}
						<img
							src={mockupMobile}
							alt="Mobile mockup Applaby"
							className="absolute -bottom-4 md:-bottom-8 left-1/4 md:left-[15%] w-[100px] md:w-[130px] lg:w-[150px] drop-shadow-2xl z-20"
						/>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
